/**
 * A plan refusal means this tab's copy of the plan is stale: re-read it, once,
 * for every surface.
 *
 * The plan is re-read only when the tab regains focus (`currentPlan.ts`,
 * `CURRENT_PLAN_QUERY_POLICY`). When the API refuses a write with `PERM003`
 * because the plan does not include the feature — the plan changed mid-session,
 * or this tab's copy was wrong — the surface that made the call says so. Every
 * OTHER surface kept offering the control until a reload: the Invites tab still offered Resend, the org menu
 * still offered "New tenant", each one a click away from the same refusal.
 *
 * So the refusal re-reads the plan, and every gate (`useEntitlement`) follows.
 * It is done HERE, on the client, rather than at the call sites: the writes
 * that can meet a plan refusal live in several packages behind several
 * adapters (`@authe/teams`' store, the org-users source, the tenancy source),
 * some with a caller that inspects the error and some without, and all of them
 * ride the one `ApiClient`. A call site added tomorrow is covered without
 * knowing this exists.
 *
 * PERM003 is the shared subscription-refusal code; new packaged features are
 * covered automatically without growing a list of feature-specific recognisers.
 */
import * as React from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useApiClient, useOptionalGraphqlClient } from "@authe/ui-core/api";
import { ErrorCode, errorCodeOf, toErrorResponse } from "@authe/fe-commons/errors";
import { CURRENT_SUBSCRIPTION_QUERY_KEY } from "./currentPlan";

/** Is `error` the API refusing something because the caller's plan lacks it? */
export function isPlanRefusal(error: unknown): boolean {
  return error != null && errorCodeOf(toErrorResponse(error).error) === ErrorCode.PERM003!.code;
}

/**
 * Re-read the plan whenever `client` resolves a plan refusal. Mounted once, by
 * `<DashboardPlan>`, beside the read it refreshes.
 */
export function useRefreshPlanOnRefusal(): void {
  const client = useApiClient();
  const graphqlClient = useOptionalGraphqlClient();
  const queryClient = useQueryClient();
  React.useEffect(() => {
    const refresh = (error: unknown): void => {
      if (!isPlanRefusal(error)) return;
      client.invalidateMatching("/pipelines");
      void queryClient.invalidateQueries({ queryKey: CURRENT_SUBSCRIPTION_QUERY_KEY });
    };
    // Both transports: a GraphQL read the plan no longer includes is refused
    // with the same PERM003, and its adapter falls back to REST without a word —
    // so without this the tab kept paying a doomed GraphQL call on every read
    // until it next regained focus.
    const offRest = client.onErrorEnvelope(refresh);
    const offGraphql = graphqlClient?.onError(refresh);
    return () => {
      offRest();
      offGraphql?.();
    };
  }, [client, graphqlClient, queryClient]);
}
